import React, { CSSProperties, forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import { IStyled } from '../../../styles/types';
import { SingleSelect, SingleSelectOption } from '../SingleSelect';
import { ISingleSelectMenuOption } from './types';
import { SvgIcon } from '../../Icon';
import BorderedDropdownLabel from '../BorderedDropdownLabel';
import { theme } from '../../../styles/theme';
import { Strategy } from '@floating-ui/react';
import { overflowingStyles } from '../../../styles/shareableStyles';

interface IProps extends IStyled {
  options: ISingleSelectMenuOption[];
  value?: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  customWidth?: CSSProperties['minWidth'];
  menuMaxHeight?: CSSProperties['maxHeight'];
  disabled?: boolean;
  isLabelWithIcon?: boolean;
  hasError?: boolean;
  strategy?: Strategy;
}

const StyledSingleSelect = styled(SingleSelect)`
  display: inline-flex;
  align-items: center;
  max-width: 240px;
`;

const StyledLabelIcon = styled(SvgIcon)`
  margin-right: 4px;
`;
const StyledText = styled.div`
  max-width: 100%;
  ${overflowingStyles};
`;

const Placeholder = styled.div`
  color: ${theme.colors.secondary400};
`;

const SingleSelectMenu = forwardRef<HTMLElement, IProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder,
      className,
      menuMaxHeight = 140,
      customWidth = '100%',
      disabled,
      isLabelWithIcon,
      hasError,
      strategy,
    },
    ref,
  ) => {
    const selectedOption = useMemo<ISingleSelectMenuOption | null>(() => {
      if (value) {
        return options.find((option) => option.value === value) ?? null;
      }
      return null;
    }, [options, value]);

    return (
      <StyledSingleSelect
        ref={ref}
        trigger={
          <BorderedDropdownLabel
            $customWidth={customWidth}
            $hasError={hasError}
            isDisabled={disabled}
          >
            {isLabelWithIcon && selectedOption && selectedOption?.iconName && (
              <StyledLabelIcon
                name={selectedOption.iconName}
                width={14}
                height={14}
                customFill="currentColor"
              />
            )}
            {selectedOption?.text ? (
              <StyledText>{selectedOption.text}</StyledText>
            ) : (
              placeholder && <Placeholder>{placeholder}</Placeholder>
            )}
          </BorderedDropdownLabel>
        }
        menuMinWidth={customWidth}
        menuMaxHeight={menuMaxHeight}
        className={className}
        disabled={disabled}
        placement="bottom-start"
        strategy={strategy}
      >
        {options.map((option) => (
          <SingleSelectOption
            key={option.value}
            label={option.text}
            onClick={() => onChange(option.value)}
            tooltipOption={option?.tooltipOption}
            iconName={option?.iconName}
            tooltipPlacement="right"
            isActive={option.value === value}
            disabled={option?.disabled}
          />
        ))}
      </StyledSingleSelect>
    );
  },
);

export default SingleSelectMenu;
